'use client';

import type { WaiverFields } from '@/lib/questions';
import { Avatar } from './Avatar';

/**
 * THE DOCUMENTS, TOGETHER, SIGNED ONCE
 * ====================================
 *
 * A waiver is not a question about a person, it is a thing the registration
 * agrees to — so it is asked once at the end, not inside each person's
 * questions. Register three students and the old form put the same page of
 * legal text in front of you three times and asked for three signatures. The
 * student retreat site settled this: documents are their own step, each one
 * says who it covers, and one typed name covers everybody in the checkout.
 *
 * MP still gets a row per person: the tick and the signature are written into
 * every registrant the document covers, because that is what the form
 * response reports on.
 *
 * The text is a document, not body copy: its own scrolling box at a size that
 * fits, with the tick and the signature underneath it, so the whole
 * transaction is on one screen instead of a wall that pushes the signature
 * off the bottom.
 */

export interface WaiverGroup {
  /** The fields, from the form these people are being asked. */
  fields: WaiverFields;
  /** Who this one covers, by draft key, with a face for each. */
  covers: { key: string; firstName: string; image: string | null }[];
  signed: boolean;
  agreed: boolean;
  signature: string;
}

export function SignStep({
  waivers,
  signer,
  onAgree,
  onSign,
}: {
  waivers: WaiverGroup[];
  /** Whose name the signature writes. */
  signer: string;
  onAgree: (group: WaiverGroup, yes: boolean) => void;
  onSign: (group: WaiverGroup, name: string) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-navy text-base font-bold">
        {waivers.length > 1 ? 'Before you go' : 'One thing to sign'}
      </p>
      {waivers.map((g) => (
        <WaiverCard
          key={g.fields.doc.id}
          group={g}
          signer={signer}
          onAgree={(yes) => onAgree(g, yes)}
          onSign={(name) => onSign(g, name)}
        />
      ))}
    </div>
  );
}

function WaiverCard({
  group,
  signer,
  onAgree,
  onSign,
}: {
  group: WaiverGroup;
  /** Whose name goes on the line: the person doing the registering. */
  signer: string;
  onAgree: (yes: boolean) => void;
  onSign: (name: string) => void;
}) {
  const { fields, covers, signature, signed } = group;
  const title = fields.doc.title ?? 'Waiver and release';
  // MP writes the whole document into one Instructions label. Break it where
  // its own numbering does, so it reads as paragraphs rather than a slab.
  const paras = (fields.doc.label ?? '').split(/\n{2,}|(?=\s\d\.\s)/g).filter((p) => p.trim());

  return (
    <section className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-navy text-sm font-bold">{title}</h3>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-bold ${
            signed ? 'bg-teal-soft text-teal-deep' : 'bg-mist text-slate'
          }`}
        >
          {signed ? 'Signed' : 'Needs a signature'}
        </span>
      </div>

      {/* Whose night this covers. One signature for a household is the point;
          the faces are how you check it is the right household. */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="kicker">Covers</span>
        {covers.map((c) => (
          <span
            key={c.key}
            className="bg-mist text-navy flex items-center gap-1.5 rounded-full py-0.5 pr-2.5 pl-0.5 text-xs font-semibold"
          >
            <Avatar name={c.firstName} image={c.image} size={20} />
            {c.firstName}
          </span>
        ))}
      </div>

      <div className="border-line bg-mist/40 scrollbar-styled mt-3 max-h-44 overflow-y-auto rounded-xl border p-3.5">
        {paras.map((p, i) => (
          <p key={i} className={`text-slate-deep text-xs leading-relaxed ${i ? 'mt-2.5' : ''}`}>
            {p.trim()}
          </p>
        ))}
      </div>

      <SignatureBox
        signer={signer}
        signature={signature}
        signed={signed}
        /* Where the document and the tick are one field, its label is
           already the paragraphs above; repeating it on the box would print
           the same sentence twice on one card. */
        statement={
          fields.agree && fields.agree.id !== fields.doc.id
            ? (fields.agree.title ?? fields.agree.label ?? 'I agree to the document above')
            : 'I agree to the above'
        }
        onSign={() => {
          if (fields.agree) onAgree(true);
          if (fields.signature) onSign(signer);
        }}
        onClear={() => {
          if (fields.agree) onAgree(false);
          if (fields.signature) onSign('');
        }}
      />
    </section>
  );
}

/**
 * ONE BOX THAT IS THE SIGNATURE AND THE AGREEMENT
 * ===============================================
 *
 * A tick and a typed name were two acts for one decision, and the typed name
 * was the worse half: a parent on a phone spelling out their own name into a
 * text field at the end of a form they have already finished. This is the
 * shape everybody already knows from signing for a parcel or adopting a
 * signature in DocuSign — a line, and one deliberate act that puts a name on
 * it.
 *
 * It is still an affirmative act, and the sentence it agrees to is written on
 * the box rather than somewhere above it, so what the tap means is where the
 * tap is. MP gets exactly what it got before: the tick in one field and the
 * name in the other, typed or not.
 *
 * The name arrives left to right rather than fading in, because that is the
 * direction writing happens and it is what makes it read as a signature
 * instead of a label switching on. Anybody who has asked their system not to
 * animate gets it immediately.
 */
function SignatureBox({
  signer,
  signature,
  signed,
  statement,
  onSign,
  onClear,
}: {
  signer: string;
  signature: string;
  signed: boolean;
  /** What signing agrees to, in the document's own words. */
  statement: string;
  onSign: () => void;
  onClear: () => void;
}) {
  const name = signature || signer;
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={signed ? undefined : onSign}
        aria-pressed={signed}
        aria-label={signed ? `Signed by ${name}` : signer ? `Sign as ${signer}` : 'Sign'}
        disabled={signed}
        className={`relative flex h-24 w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed px-4 transition ${
          signed
            ? 'border-teal/40 bg-teal-soft/40 cursor-default'
            : 'border-line bg-paper hover:border-teal hover:bg-mist/40 active:scale-[0.99]'
        }`}
      >
        {signed ? (
          <span className="signed-name font-hand text-navy text-4xl leading-none whitespace-nowrap">
            {name}
          </span>
        ) : (
          <span className="text-slate text-sm font-semibold">
            {signer ? `Tap to sign as ${signer}` : 'Tap to sign'}
          </span>
        )}
        {/* The line stays under both states: it is what says this is a place a
            name goes, before there is one on it. */}
        <span className="border-line absolute right-6 bottom-5 left-6 border-b" />
      </button>
      <div className="mt-2 flex items-start gap-2">
        <p className="text-slate flex-1 text-xs leading-relaxed">{statement}</p>
        {signed && (
          <button
            type="button"
            onClick={onClear}
            className="text-slate hover:text-navy shrink-0 text-xs font-semibold hover:underline"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
